/* eslint-disable @typescript-eslint/no-use-before-define */
import { LinkButton } from '@euler/app/components/LinkButton';
import { useMemberPickerBottomSheetContext } from '@euler/app/components/MemberPickerBottomSheet';
import { Typography } from '@euler/app/components/typography';
import { StatusColors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetHeight } from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { OrgUserRoleType } from '@euler/model/enum';
import { Assignee } from '@euler/model/task-detail';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import BottomSheet, { BottomSheetFlatList } from '@gorhom/bottom-sheet';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { FlatList, Platform, TouchableOpacity, View } from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject, Subject } from 'rxjs';
import { useTheme } from 'styled-components';
import { useScheduleConstructionJobs } from '../ConstructionJobScheduleContext';
import { ConstructionWorkItemInfo } from '../functions/useConstructionWorkItems';
import { WorkItemCell } from './WorkItemCell';

const kHandleHeight = 24;
const kCellHeight = 44;

export type WorkItemsBottomSheetAction = {
  type: 'reveal';
  index?: number;
};

export const useWorkItemsBottomSheetFooterHeight = () => {
  const insets = useSafeAreaInsets();
  const { rpx } = useResponsiveContext();
  return rpx(54) + Math.max(rpx(10), insets.bottom) + kHandleHeight;
};

export const SelectedWorkItemsBottomSheet = memo(
  ({
    action$,
    onDone,
  }: {
    action$: Subject<WorkItemsBottomSheetAction>;
    onDone?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { navBarHeight } = useSystemMetrics();
    const confirm = useConfirm();
    const height = useGeneralBottomSheetHeight();
    const footerHeight = useWorkItemsBottomSheetFooterHeight();
    const bottomSheetRef = useRef<BottomSheet>(null);
    const minSnapPoint = footerHeight;
    const lowerWatermarkSnapPoint = rpx(kCellHeight) * 3 + minSnapPoint;
    const mediumWatermarkSnapPoint = rpx(kCellHeight) * 10 + minSnapPoint;
    const maxSnapPoint = height - navBarHeight;
    const snapPoints = useMemo(
      () => [
        minSnapPoint,
        lowerWatermarkSnapPoint,
        mediumWatermarkSnapPoint,
        maxSnapPoint,
      ],
      [
        lowerWatermarkSnapPoint,
        maxSnapPoint,
        mediumWatermarkSnapPoint,
        minSnapPoint,
      ],
    );

    const { memberPickerPresentationRequest$ } =
      useMemberPickerBottomSheetContext();

    const {
      selectedWorkItems,
      toggleWorkItem,
      clearWorkItems,
      updateWorkItemsAssignee,
    } = useScheduleConstructionJobs();

    const animatedIndex = useSharedValue(0);
    const animatedPosition = useSharedValue(0);
    const translateYOffset = rpx(50);

    const animatedEmptyStyle = useAnimatedStyle(() => {
      const visibleHeight = interpolate(
        animatedPosition.value,
        [
          height - maxSnapPoint,
          height - mediumWatermarkSnapPoint,
          height - lowerWatermarkSnapPoint,
          height - minSnapPoint,
        ],
        [
          maxSnapPoint - minSnapPoint,
          mediumWatermarkSnapPoint - minSnapPoint,
          lowerWatermarkSnapPoint - minSnapPoint,
          0,
        ],
      );
      return {
        opacity: interpolate(
          animatedPosition.value,
          [
            height - maxSnapPoint,
            height - mediumWatermarkSnapPoint,
            height - lowerWatermarkSnapPoint,
            height - minSnapPoint,
          ],
          [1, 1, 1, 0],
        ),
        transform: [
          {
            translateY: visibleHeight / 2 - translateYOffset,
          },
          {
            scale: interpolate(
              animatedPosition.value,
              [
                height - maxSnapPoint,
                height - mediumWatermarkSnapPoint,
                height - lowerWatermarkSnapPoint,
                height - minSnapPoint,
              ],
              [1, 1, 1, 0],
            ),
          },
        ],
      };
    });

    const checked$ = useCreation(
      () => new BehaviorSubject<ConstructionWorkItemInfo[]>([]),
      [],
    );
    const [checked, setChecked] = useBehaviorSubject(checked$);

    const isAllChecked =
      checked.length > 0 && checked.length === selectedWorkItems.length;

    const hasUnassignedItems = selectedWorkItems.some(x => !x.assignee);

    const onItemPress = usePersistFn((workItem: ConstructionWorkItemInfo) => {
      const index = checked.indexOf(workItem);
      if (index >= 0) {
        setChecked(l => l.filter(x => x !== workItem));
      } else {
        setChecked(l => [...l, workItem]);
      }
    });

    const onRemoveItem = useCallback(
      (item: ConstructionWorkItemInfo) => {
        toggleWorkItem(item);
        setChecked(l => l.filter(x => x !== item));
      },
      [setChecked, toggleWorkItem],
    );

    const onSelectedButtonPress = useCallback(() => {
      bottomSheetRef.current?.snapToIndex(2);
    }, []);

    const onToggleSelectAll = usePersistFn(() => {
      if (isAllChecked) {
        checked$.next([]);
      } else {
        checked$.next(selectedWorkItems.map(x => x.workItem));
      }
    });

    const onClear = usePersistFn(async () => {
      if (
        await confirm({
          title: '确认',
          message: '是否确认清空全部施工项目?',
        })
      ) {
        checked$.next([]);
        clearWorkItems();
      }
    });

    useEffect(() => {
      const sub = action$.subscribe(action => {
        if (action.type === 'reveal') {
          bottomSheetRef.current?.snapToIndex(action.index ?? 2);
        }
      });
      return () => sub.unsubscribe();
    }, [action$]);

    const onAssignButtonPress = usePersistFn(() => {
      memberPickerPresentationRequest$.next({
        title: '施工项目派工',
        role: OrgUserRoleType.Technicians,
        onSelect: member => {
          memberPickerPresentationRequest$.next(undefined);
          updateWorkItemsAssignee(checked, {
            id: member.id,
            name: member.name,
          });
          setChecked([]);
        },
      });
    });
    // BottomSheet specific scroll view components do not work on Web right now.
    // see https://github.com/gorhom/react-native-bottom-sheet/issues/11.
    const ListComponent =
      Platform.OS === 'web' ? FlatList : BottomSheetFlatList;

    return (
      <>
        <BottomSheet
          ref={bottomSheetRef}
          snapPoints={snapPoints}
          animatedIndex={animatedIndex}
          animatedPosition={animatedPosition}
          index={0}
          css={`
            background-color: #fff;
            box-shadow: 0 -1px ${rpx(15)}px rgba(0, 0, 0, 0.5);
            elevation: 3;
          `}
          style={{
            elevation: 3,
          }}
        >
          <ListComponent
            data={selectedWorkItems}
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: footerHeight,
            }}
            ListEmptyComponent={
              <Animated.View
                css={`
                  height: ${rpx(100)}px;
                `}
                style={animatedEmptyStyle}
              >
                <EmptyView message="尚未选择任何施工项目" />
              </Animated.View>
            }
            ListHeaderComponent={
              selectedWorkItems.length > 0 ? (
                <HeaderComponent
                  isAllChecked={isAllChecked}
                  onToggleSelectAll={onToggleSelectAll}
                  onClear={onClear}
                />
              ) : undefined
            }
            renderItem={({ item, index }) => (
              <Cell
                item={item.workItem}
                assignee={item.assignee}
                selected={selectedWorkItems.some(
                  x => x.workItem === item.workItem,
                )}
                checked={checked.includes(item.workItem)}
                onPress={onItemPress}
                onRemove={onRemoveItem}
                first={index === 0}
                last={index === selectedWorkItems.length - 1}
              />
            )}
          />
        </BottomSheet>
        <BottomBar
          count={selectedWorkItems.length}
          checkedCount={checked.length}
          hasUnassignedItems={hasUnassignedItems}
          onSelectedButtonPress={onSelectedButtonPress}
          onDoneButtonPress={onDone}
          onAssignButtonPress={onAssignButtonPress}
        />
      </>
    );
  },
);

const HeaderComponent = memo(
  ({
    isAllChecked,
    onToggleSelectAll,
    onClear,
  }: {
    isAllChecked: boolean;
    onToggleSelectAll: () => void;
    onClear: () => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    return (
      <HStack
        justifyContent="flex-start"
        css={`
          padding: 0 ${rpx(17.5)}px;
          margin-bottom: ${rpx(15)}px;
        `}
      >
        <Icon name="information-solid" size={rpx(16)} color={theme.link} />
        <Typography.Label
          bold
          color="#777"
          size={rpx(16)}
          lineHeight={rpx(20)}
          css={`
            margin-left: ${rpx(4)}px;
            flex: 1;
          `}
        >
          可选择项目进行派工
        </Typography.Label>
        <LinkButton
          text={isAllChecked ? '取消全选' : '全选'}
          onPress={onToggleSelectAll}
        />
        <LinkButton
          text={'清空'}
          onPress={onClear}
          css={`
            margin-left: ${rpx(8)}px;
          `}
        />
      </HStack>
    );
  },
);

const BottomBar = memo(
  ({
    count,
    checkedCount,
    hasUnassignedItems,
    onSelectedButtonPress,
    onDoneButtonPress,
    onAssignButtonPress,
  }: {
    count: number;
    checkedCount: number;
    hasUnassignedItems: boolean;
    onSelectedButtonPress?: () => void;
    onDoneButtonPress?: () => void;
    onAssignButtonPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const theme = useTheme();

    const paddingTop = rpx(10);
    const paddingBottom = Math.max(rpx(10), insets.bottom);
    const minHeight = rpx(44) + paddingTop + paddingBottom;

    return (
      <View
        css={`
          padding-top: ${paddingTop}px;
          padding-bottom: ${paddingBottom}px;
          min-height: ${minHeight}px;
          background-color: #fff;
          elevation: 1;
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
        `}
      >
        <HStack
          css={`
            padding: 0 ${rpx(15)}px;
            flex: 1;
          `}
        >
          <TouchableOpacity
            onPress={onSelectedButtonPress}
            css={`
              background-color: #ccc;
              height: ${rpx(44)}px;
              border-radius: ${rpx(22)}px;
              padding: 0 ${rpx(22)}px;
              align-items: center;
              justify-content: center;
              flex-direction: row;
            `}
          >
            <Icon name="clipboard" size={rpx(16)} color={'#333'} />
            <Typography.Label color={'#333'} size={rpx(16)}>
              {`已选 (${count})`}
            </Typography.Label>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={
              checkedCount > 0 && hasUnassignedItems
                ? onAssignButtonPress
                : onDoneButtonPress
            }
            css={`
              flex: 1;
              margin-left: ${rpx(10)}px;
              height: ${rpx(44)}px;
              border-radius: ${rpx(22)}px;
              align-items: center;
              justify-content: center;
              background-color: ${checkedCount > 0 && hasUnassignedItems
                ? StatusColors.Danger
                : theme.link};
            `}
          >
            <Typography.Label size={rpx(20)} color="#fff" regular>
              {checkedCount > 0 && hasUnassignedItems ? `为项目派工` : '完成'}
            </Typography.Label>
          </TouchableOpacity>
        </HStack>
      </View>
    );
  },
);

const Cell = memo(
  ({
    item,
    assignee,
    selected,
    checked,
    onPress,
    onRemove,
  }: {
    first?: boolean;
    last?: boolean;
    item: ConstructionWorkItemInfo;
    assignee?: Assignee;
    checked?: boolean;
    selected: boolean;
    onPress: (item: ConstructionWorkItemInfo) => void;
    onRemove: (item: ConstructionWorkItemInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      construction: {
        schedule: {
          workItem: {
            cell: { textLight, bgLight },
          },
        },
      },
    } = useTheme();

    const onCellPress = useCallback(() => {
      onPress?.(item);
    }, [item, onPress]);

    const handleRemove = useCallback(() => {
      onRemove?.(item);
    }, [item, onRemove]);

    return (
      <WorkItemCell
        title={item.name}
        selected={selected}
        checked={checked}
        showAssignee
        assignee={assignee}
        onPress={onCellPress}
        onRemove={handleRemove}
        titleStyle={{
          color: !assignee ? StatusColors.Danger : textLight,
          fontFamily: FontFamily.NotoSans.Light,
          fontSize: rpx(16),
        }}
        css={`
          background-color: ${bgLight};
          padding-top: ${rpx(5)}px;
          padding-bottom: ${rpx(5)}px;
          padding-left: ${rpx(15)}px;
          padding-right: ${rpx(8)}px;
          margin: 0;
          border-bottom-width: 1px;
          border-bottom-color: #eee;
          border-radius: 0;
        `}
      />
    );
  },
);
